import React, { useEffect, useState, useRef } from 'react';
import { clickable, personColor, useDrawerFocus } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

function Avatar({ name }) {
  const initials = (name || '?').split(' ').filter(w => w.length > 1).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color = personColor(name);
  return (
    <div style={{ width: 40, height: 40, borderRadius: '50%', background: color + '22',
      color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, flexShrink: 0, border: `2px solid ${color}44` }}>
      {initials}
    </div>
  );
}

function Delta({ pct, invert }) {
  if (pct === null || pct === undefined) return <span style={{ fontSize: 11, color: 'var(--text-2)' }}>vs prev —</span>;
  const up = pct >= 0;
  const good = invert ? !up : up;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: good ? 'var(--ok)' : 'var(--critical)',
      display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width .4s ease' }} />
    </div>
  );
}

function MemberCard({ member, rank, maxTickets, maxHours, maxResol, onWeekClick }) {
  const color = personColor(member.name);
  const rankColors = ['#f59e0b','#9ca3af','#b45309'];

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
      padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
      {/* Rank badge */}
      {rank <= 3 && (
        <div style={{ position: 'absolute', top: 14, right: 16, width: 28, height: 28, borderRadius: '50%',
          background: rankColors[rank - 1] + '22', color: rankColors[rank - 1],
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, border: `2px solid ${rankColors[rank - 1]}44` }}>
          {rank}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <Avatar name={member.name} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{member.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>#{rank} this month</div>
        </div>
      </div>

      {/* Tickets closed */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Tickets closed</div>
          <Delta pct={member.tickets_delta_pct} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color }}>{member.tickets_cur}</span>
          <div style={{ flex: 1 }}>
            <Bar value={member.tickets_cur} max={maxTickets} color={color} />
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>
            {member.tickets_prev} prev
          </span>
        </div>
      </div>

      {/* Hours logged */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Hours logged</div>
          <Delta pct={member.hours_delta_pct} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: '#7c3aed' }}>{member.hours_cur_display}</span>
          <div style={{ flex: 1 }}>
            <Bar value={member.hours_cur} max={maxHours} color="#7c3aed" />
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>
            {member.hours_prev_display} prev
          </span>
        </div>
      </div>

      {/* Avg resolution time */}
      {member.resol_cur_days !== null && member.resol_cur_days !== undefined && member.resol_cur_days > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Avg resolution</div>
            <Delta pct={member.resol_delta_pct} invert />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: '#059669' }}>{member.resol_cur_display}</span>
            <div style={{ flex: 1 }}>
              <Bar value={member.resol_cur_days || 0} max={maxResol} color="#05966944" />
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>
              {member.resol_prev_display} prev
            </span>
          </div>
        </div>
      )}

      {/* This week */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', gap: 0 }}>
        <div {...clickable(() => onWeekClick(member.name, 'tickets'))} aria-label={`${member.name}: tickets closed this week`}
          style={{ flex: 1, paddingRight: 12, borderRight: '1px solid var(--border)',
          cursor: 'pointer', borderRadius: 7, padding: '8px 12px 8px 0', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>This week · tickets ↗</div>
          <span style={{ fontSize: 22, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color }}>{member.week_tickets ?? 0}</span>
        </div>
        <div {...clickable(() => onWeekClick(member.name, 'hours'))} aria-label={`${member.name}: hours logged this week`}
          style={{ flex: 1, paddingLeft: 12,
          cursor: 'pointer', borderRadius: 7, padding: '8px 0 8px 12px', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>This week · hours <span style={{ fontWeight: 400, textTransform: 'none' }}>(excl. ABE)</span> ↗</div>
          <span style={{ fontSize: 22, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: '#7c3aed' }}>{member.week_hours_display ?? '0h'}</span>
        </div>
      </div>
    </div>
  );
}

function WeekModal({ member, type, weekLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const dialogRef = useRef(null);
  useDrawerFocus(dialogRef);

  useEffect(() => {
    setLoading(true); setError(false);
    fetch(`${API}/api/performance/week-detail?member_name=${encodeURIComponent(member)}&type=${type}`)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [member, type]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const title = type === 'tickets'
    ? `Tickets closed this week — ${member.split(' ')[0]}`
    : `Hours logged this week — ${member.split(' ')[0]} (excl. ABE)`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 720, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={dialogRef}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
            <div style={{ color: 'var(--text-2)', fontSize: 12, marginTop: 2 }}>
              {weekLabel}{data && type === 'hours' && ` · Total: ${data.total_display}`}
              {data && ` · ${data.items?.length} ${type === 'tickets' ? 'tickets' : 'entries'}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-2)', padding: 4 }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)' }}>Loading…</div>}
          {!loading && error && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}>Couldn’t load this week’s detail — check the connection and reopen.</div>
          )}
          {!loading && !error && data?.items?.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)' }}>No data for this week.</div>
          )}
          {!loading && !error && data?.items?.map((item, i) => (
            <div key={i} style={{ display: 'grid',
              gridTemplateColumns: type === 'tickets' ? '1fr auto' : '1fr auto',
              alignItems: 'center', gap: 12, padding: '11px 22px',
              borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3, flexWrap: 'wrap' }}>
                  {item.url
                    ? <a href={item.url} target="_blank" rel="noopener noreferrer" className="badge badge-blue" style={{ textDecoration: 'none', fontSize: 11 }}>{item.issue_key}</a>
                    : <span className="badge badge-blue" style={{ fontSize: 11 }}>{item.issue_key}</span>}
                  {item.status && <span className="badge badge-default" style={{ fontSize: 10 }}>{item.status}</span>}
                  {item.customer && <span style={{ color: 'var(--text-2)', fontSize: 11 }}>{item.customer}</span>}
                  {type === 'hours' && item.logged_date && (
                    <span style={{ color: 'var(--text-2)', fontSize: 11 }}>
                      {new Date(`${item.logged_date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                      {item.started_time && ` · ${item.started_time}`}
                    </span>
                  )}
                  {type === 'tickets' && item.resolved_at && (
                    <span style={{ color: 'var(--text-2)', fontSize: 11 }}>
                      {new Date(`${item.resolved_at}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{item.summary}</div>
              </div>
              {type === 'hours' && (
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 14 }}>{item.display}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PerformancePage() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const load = (m) => {
    setLoading(true); setError(null);
    fetch(`${API}/api/performance?month=${m}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(month); }, [month]);

  const changeMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    let nm = m + delta, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1)  { nm = 12; ny--; }
    setMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const members    = data?.members || [];
  const maxTickets = Math.max(...members.map(m => m.tickets_cur), 1);
  const maxHours   = Math.max(...members.map(m => m.hours_cur), 1);
  const maxResol   = Math.max(...members.map(m => m.resol_cur_days || 0), 1);

  const teamTicketsDelta = data
    ? (data.team_tickets_prev > 0
        ? Math.round(((data.team_tickets_cur - data.team_tickets_prev) / data.team_tickets_prev) * 100)
        : null)
    : null;

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => changeMonth(-1)}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 7,
            background: 'var(--surface)', cursor: 'pointer', fontSize: 16, color: 'var(--text)' }}>‹</button>
        <span style={{ fontSize: 16, fontWeight: 700, minWidth: 130, textAlign: 'center' }}>
          {data?.label || month}
        </span>
        <button onClick={() => changeMonth(1)}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 7,
            background: 'var(--surface)', cursor: 'pointer', fontSize: 16, color: 'var(--text)' }}>›</button>
        <button onClick={() => load(month)}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 7,
            background: 'var(--surface)', cursor: 'pointer', fontSize: 12, color: 'var(--text-2)' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Team summary cards */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          <div className="card">
            <div className="card-title">Team Tickets Closed</div>
            <div className="card-value" style={{ color: 'var(--accent)' }}>{data.team_tickets_cur}</div>
            <div className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Delta pct={teamTicketsDelta} />
              <span>vs {data.prev_period_label}</span>
            </div>
          </div>
          <div className="card">
            <div className="card-title">Prev Period Tickets</div>
            <div className="card-value">{data.team_tickets_prev}</div>
            <div className="card-sub">{data.prev_period_label}</div>
          </div>
          <div className="card">
            <div className="card-title">Team Hours Logged</div>
            <div className="card-value" style={{ fontSize: 22, fontFamily: "'IBM Plex Mono', monospace", color: '#7c3aed' }}>{data.team_hours_cur}</div>
            <div className="card-sub">{data.period_label}</div>
          </div>
          <div className="card">
            <div className="card-title">Prev Period Hours</div>
            <div className="card-value" style={{ fontSize: 22, fontFamily: "'IBM Plex Mono', monospace" }}>{data.team_hours_prev}</div>
            <div className="card-sub">{data.prev_period_label}</div>
          </div>
          <div className="card">
            <div className="card-title">Avg Resolution</div>
            <div className="card-value" style={{ fontSize: 22, fontFamily: "'IBM Plex Mono', monospace", color: '#059669' }}>
              {data.team_resol_cur_display || '—'}
            </div>
            <div className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Delta pct={data.team_resol_delta_pct} invert />
              <span>{data.team_resol_prev_display || '—'} prev</span>
            </div>
          </div>
        </div>
      )}

      {/* Per-member grid */}
      {loading ? (
        <div className="loading">Loading performance data…</div>
      ) : error ? (
        <div className="empty"><p style={{ color: 'var(--critical)' }}>{error}</p></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {members.map((m, i) => (
            <MemberCard key={m.name} member={m} rank={i + 1} maxTickets={maxTickets} maxHours={maxHours} maxResol={maxResol}
              onWeekClick={(name, type) => setSelectedWeek({ member: name, type })} />
          ))}
        </div>
      )}

      {data && (
        <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-2)', textAlign: 'right' }}>
          Comparing {data.period_label} vs {data.prev_period_label} · ranked by tickets closed · week: {data.week_label}
        </div>
      )}

      {selectedWeek && (
        <WeekModal
          member={selectedWeek.member}
          type={selectedWeek.type}
          weekLabel={data?.week_label || ''}
          onClose={() => setSelectedWeek(null)}
        />
      )}
    </div>
  );
}
